import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { ExternalLink, AlertTriangle, ArrowRight, Clock, Building2 } from "lucide-react";
import { formatEUR } from "../lib/api";
import { toast } from "sonner";

/**
 * Iteration 8 — Bank transfer step is now handled at XGate's themed checkout.
 * We disclose the payment flow, show the merchant_reference for tracing,
 * and hand off to XGate's hosted page. The customer gets the actual vIBAN there.
 *
 * Iteration 11 — visible quote countdown: surface XGate's 15-min invoice clock so the
 * user knows they need to act before it expires.
 */
export default function SepaInstructions({ order, onContinue }) {
  // XGate invoices are valid 15 minutes from order creation. We use created_at
  // (server clock) and tick down so the user sees how long they have.
  const expiresAt = order.created_at
    ? new Date(new Date(order.created_at).getTime() + 15 * 60 * 1000).getTime()
    : Date.now() + 15 * 60 * 1000;
  const [remaining, setRemaining] = useState(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)));
  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const expiringSoon = remaining < 60;
  const expired = remaining === 0;

  const openXGate = () => {
    // Iter46 — prefer the operator-pasted xgate_payment_url (set via admin
    // after they create the invoice in app.xgateway.tech). Falls back to
    // the legacy checkout_url if present.
    const url = order.xgate_payment_url || order.checkout_url;
    if (!url) {
      toast.info("Your bank details are being prepared — we'll email you shortly (usually within one business day).");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opened XGate checkout in a new tab");
  };

  // Iter46 — surface the "awaiting bank details" state up-front when ops
  // hasn't yet pasted the XGate Payment URL. Without this the CTA looks
  // broken (customer clicks, nothing happens).
  const awaitingXGateInvoice = !order.xgate_payment_url && !order.checkout_url;

  return (
    <div className="space-y-5" data-testid="sepa-instructions">
      <div>
        <h2 className="font-display text-2xl font-bold text-primary">Pay {formatEUR(order.fiat_amount)} by SEPA</h2>
        <p className="text-sm text-slate-500 mt-1">
          You'll be handed off to our regulated banking partner to complete the transfer. Returning customers
          will see prefilled details — usually one step.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex gap-2.5 text-sm" data-testid="sepa-warning">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-amber-900">
          <strong>Heads up:</strong> if you're on a VPN or Apple Private Relay, identity verification will block.
          Disable them before continuing.
        </div>
      </div>

      <div className="rounded-2xl border border-primary/20 bg-primary/[0.02] p-4 space-y-3" data-testid="xgate-handoff-card">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <div className="text-[10px] uppercase tracking-wider font-bold text-primary">Banking partner</div>
        </div>
        <div>
          <div className="text-sm text-foreground/80">
            The virtual IBAN and SEPA flow is facilitated through our regulated payment partner.
          </div>
          <div className="text-[11px] text-muted-foreground mt-1.5">
            Your order reference is shown on the next screen — keep it for support.
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl bg-white border border-border px-3 py-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Reference</div>
            <div className="font-mono font-bold text-foreground tabular-nums" data-testid="xgate-merchant-reference">{order.reference}</div>
          </div>
          <div className={`flex items-center gap-1 text-[11px] font-mono font-semibold tabular-nums ${
            expired ? "text-rose-600" : expiringSoon ? "text-amber-600" : "text-muted-foreground"
          }`} data-testid="xgate-invoice-countdown">
            <Clock className="w-3 h-3" />
            {expired ? "Invoice expired — restart order" : `Invoice expires in ${mm}:${ss}`}
          </div>
        </div>
      </div>

      <Button
        onClick={openXGate}
        disabled={awaitingXGateInvoice}
        className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-12 font-display font-semibold gap-2 disabled:opacity-60"
        data-testid="open-xgate-btn"
      >
        {awaitingXGateInvoice
          ? "Awaiting bank details (we&apos;ll email you)"
          : <>Continue to bank transfer <ExternalLink className="w-4 h-4" /></>}
      </Button>

      {awaitingXGateInvoice && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900"
              data-testid="sepa-awaiting-invoice">
          <strong>What happens next:</strong> our ops team is generating
          your personalised wire instructions at XGate. You&apos;ll receive an
          email with a &ldquo;Continue to payment&rdquo; link the moment it&apos;s
          ready — usually within one business day. You can leave this page.
        </div>
      )}

      <Button
        onClick={onContinue}
        variant="ghost"
        className="w-full text-foreground hover:bg-muted rounded-xl gap-2"
        data-testid="sepa-track-btn"
      >
        Track this order <ArrowRight className="w-4 h-4" />
      </Button>

      <p className="text-[11px] text-muted-foreground text-center">
        The virtual IBAN and crypto swap are facilitated through our regulated payment partner.
        Your crypto is sent to your whitelisted wallet only after the EUR deposit is confirmed.
      </p>
    </div>
  );
}
